/**
 * Checkout result page — used for /checkout/success, /checkout/pending
 * and /checkout/failure. Polls the order status from the backend so the
 * UI shows the authoritative state derived from the Mercado Pago webhook
 * (not just the query params, which can be stale).
 */
import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Clock, XCircle, ArrowLeft, Storefront as StoreIcon, MapPin, Receipt } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmtMXN = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CheckoutResult() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  // MP usually appends external_reference; some integrations don't.
  const externalRef = params.get("external_reference") || params.get("order_id") || "";
  const initialKind = pathname.endsWith("/success") ? "success" : pathname.endsWith("/pending") ? "pending" : "failure";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(!!externalRef);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Poll the backend up to ~10 times every 2s while the webhook catches up
  useEffect(() => {
    if (!externalRef) return;
    let cancelled = false;
    let timer;
    const fetchOrder = async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/checkout/order/${externalRef}`);
        if (cancelled) return;
        setOrder(data);
        setLoading(false);
        // Keep polling if still pending and we expected success
        if (initialKind === "success" && data.status !== "paid" && attempts < 10) {
          timer = setTimeout(() => setAttempts((a) => a + 1), 2000);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOrder();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [externalRef, attempts, initialKind]);

  // Resolve the final visual kind from authoritative order status when available
  const kind = order ? (
    order.status === "paid" ? "success"
      : (order.status === "pending" || order.status === "unknown") ? "pending"
      : "failure"
  ) : initialKind;

  const palette = {
    success: { icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", title: "¡Pago aprobado!", sub: "Gracias por tu compra. Te enviamos un correo con la confirmación." },
    pending: { icon: Clock, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", title: "Pago en proceso", sub: "Mercado Pago todavía está confirmando tu pago. Te avisaremos por correo cuando se acredite." },
    failure: { icon: XCircle, color: "text-red-600", bg: "bg-red-50", border: "border-red-200", title: "El pago no se completó", sub: "El pago fue rechazado o cancelado. Puedes intentar de nuevo." },
  }[kind];

  const Icon = palette.icon;

  return (
    <div className="min-h-screen bg-[#FAF8F2]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/catalogo" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-900 mb-6" data-testid="result-back">
          <ArrowLeft size={14} weight="bold" /> Volver al catálogo
        </Link>

        <div className={`rounded-3xl border-2 ${palette.border} ${palette.bg} p-8 sm:p-12 text-center`} data-testid={`result-card-${kind}`}>
          <div className={`mx-auto w-16 h-16 rounded-full bg-white inline-flex items-center justify-center mb-4 ${palette.color}`}>
            <Icon size={32} weight="duotone" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-zinc-900">{palette.title}</h1>
          <p className="text-zinc-600 mt-3 max-w-md mx-auto">{palette.sub}</p>

          {loading && (
            <p className="text-xs text-zinc-500 mt-4">Sincronizando con Mercado Pago…</p>
          )}

          {order && order.sale_folio && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-zinc-200 text-sm font-mono" data-testid="result-folio">
              <Receipt size={14} /> Folio <span className="font-bold">#{order.sale_folio}</span>
            </div>
          )}
        </div>

        {order && order.items?.length > 0 && (
          <div className="mt-8 bg-white border border-zinc-100 rounded-2xl p-6 shadow-sm">
            <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-3">Tu pedido</h2>
            <div className="space-y-3">
              {order.items.map((it, idx) => (
                <div key={idx} className="flex gap-3 text-sm">
                  <div className="w-12 h-12 rounded-md bg-zinc-50 border border-zinc-100 overflow-hidden flex items-center justify-center shrink-0">
                    {it.photo ? <img src={it.photo} alt={it.product_name} className="w-full h-full object-contain" /> : <span className="text-[9px] text-zinc-300">Sin foto</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium leading-tight line-clamp-2">{it.product_name}</div>
                    <div className="text-[11px] text-zinc-500 numeric">{it.qty} × {fmtMXN(it.unit_price_mxn)}</div>
                  </div>
                  <div className="text-sm font-semibold numeric whitespace-nowrap">{fmtMXN(it.subtotal_mxn)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-zinc-100 text-sm space-y-1">
              <div className="flex justify-between text-zinc-600"><span>Subtotal</span><span className="numeric">{fmtMXN(order.items_subtotal_mxn)}</span></div>
              <div className="flex justify-between text-zinc-600"><span>Envío</span><span className="numeric">{order.shipping_cost_mxn > 0 ? fmtMXN(order.shipping_cost_mxn) : "—"}</span></div>
              <div className="flex justify-between pt-2 border-t border-zinc-100 items-baseline">
                <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Total</span>
                <span className="font-heading text-2xl font-extrabold text-zinc-900 numeric">{fmtMXN(order.total_mxn)}</span>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-zinc-100 text-xs text-zinc-600 inline-flex items-center gap-2">
              {order.delivery === "pickup" ? (
                <><StoreIcon size={14} weight="duotone" className="text-emerald-600" /> Recoger en tienda — te contactaremos para coordinar.</>
              ) : (
                <><MapPin size={14} weight="duotone" className="text-[#9b6f00]" /> Envío a: {order.shipping_address}</>
              )}
            </div>
          </div>
        )}

        {kind === "failure" && (
          <div className="text-center mt-6">
            <Link to="/catalogo" className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-zinc-900 hover:bg-zinc-800 text-white font-semibold text-sm" data-testid="result-retry-cta">
              Intentar otra compra
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
